// Global keyboard shortcut system with support for single keys and key sequences

import { useEffect, useRef, useCallback } from 'react';

export interface Shortcut {
  /** Key or key sequence (e.g., 'j', 'Escape', ['g', 'd']) */
  keys: string | string[];
  /** Handler to call when the shortcut is triggered */
  handler: () => void;
  /** Description for the help overlay */
  description: string;
  /** Group name for the help overlay */
  group: string;
  /** Whether this shortcut should work even when a modal is open */
  allowInModal?: boolean;
}

const IGNORE_ELEMENTS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

function isEditableElement(el: EventTarget | null): boolean {
  if (!el || !(el instanceof HTMLElement)) return false;
  if (IGNORE_ELEMENTS.has(el.tagName)) return true;
  if (el.isContentEditable) return true;
  return false;
}

function isModalOpen(): boolean {
  // Radix UI dialogs use [data-state="open"] on [role="dialog"]
  return document.querySelector('[role="dialog"][data-state="open"]') !== null;
}

/**
 * Register keyboard shortcuts. Handles single keys and two-key sequences (e.g., g then d).
 * Automatically disables shortcuts when focus is in form inputs or when modals are open.
 */
export function useKeyboardShortcuts(shortcuts: Shortcut[]) {
  const pendingPrefix = useRef<string | null>(null);
  const prefixTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const clearPrefix = useCallback(() => {
    pendingPrefix.current = null;
    if (prefixTimer.current) {
      clearTimeout(prefixTimer.current);
      prefixTimer.current = null;
    }
  }, []);

  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      // Skip if typing in an input
      if (isEditableElement(e.target)) return;

      const modalOpen = isModalOpen();
      const key = e.key;

      // Check for sequence completions first (e.g., pending 'g' + current 'd')
      if (pendingPrefix.current !== null) {
        const prefix = pendingPrefix.current;
        clearPrefix();

        for (const shortcut of shortcuts) {
          if (!Array.isArray(shortcut.keys)) continue;
          if (shortcut.keys.length !== 2) continue;
          if (shortcut.keys[0] !== prefix || shortcut.keys[1] !== key) continue;
          if (modalOpen && !shortcut.allowInModal) continue;

          e.preventDefault();
          shortcut.handler();
          return;
        }
        // Prefix didn't match any sequence — fall through to single-key check
      }

      // Check for sequence starters
      const isSequencePrefix = shortcuts.some(
        (s) => Array.isArray(s.keys) && s.keys.length === 2 && s.keys[0] === key
      );
      if (isSequencePrefix) {
        pendingPrefix.current = key;
        prefixTimer.current = setTimeout(clearPrefix, 1000);
        return;
      }

      // Check single-key shortcuts
      for (const shortcut of shortcuts) {
        if (Array.isArray(shortcut.keys)) continue;
        if (shortcut.keys !== key) continue;
        if (modalOpen && !shortcut.allowInModal) continue;

        e.preventDefault();
        shortcut.handler();
        return;
      }
    }

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      clearPrefix();
    };
  }, [shortcuts, clearPrefix]);
}
